import { useState } from "react";
import { X, Loader2, ThumbsUp, ThumbsDown, AlertCircle, Gavel } from "lucide-react";
import { useAdminRecordCommitteeDecisionMutation } from "../api/upgradeApi";
import { describeApiError } from "../../../lib/apiError";

/**
 * Record a Membership Committee member's recommendation on their behalf.
 *
 * The committee does not sit at a keyboard. A recommendation is given at the
 * meeting, signed on the assessment form, or phoned in — and without this the
 * secretariat could only wait for the member to log in and key it themselves,
 * with the applicant's induction bill waiting behind it.
 *
 * So the dialog is written as a transcription, not as a decision: it names
 * whose recommendation is being entered, and the remark field is where the
 * reason they gave goes. The recommendation remains theirs on the record — the
 * admin's name is kept beside it as the person who typed it, nothing more.
 */
export default function RecordCommitteeDecisionModal({
  applicationId,
  applicantName,
  review,
  onClose,
  onDone,
}: {
  applicationId: string | number;
  applicantName?: string;
  /** The assignment row being decided: { id, name, decision, remark, recorded_by_name }. */
  review: any;
  onClose: () => void;
  onDone?: (message: string) => void;
}) {
  const [decision, setDecision] = useState<"approved" | "rejected" | "">(
    review?.decision === "approved" || review?.decision === "rejected" ? review.decision : "",
  );
  const [remark, setRemark] = useState<string>(review?.remark ?? "");
  const [err, setErr] = useState<string | null>(null);

  const [record, { isLoading: saving }] = useAdminRecordCommitteeDecisionMutation();

  // A row that already carries a decision is being CORRECTED, not decided, and
  // the dialog has to say so — the admin is about to overwrite something a
  // committee member may have recorded themselves.
  const already = review?.decision === "approved" || review?.decision === "rejected";

  const submit = async () => {
    if (!decision) return;
    setErr(null);

    try {
      const res: any = await record({
        id: applicationId,
        reviewId: review.id,
        decision,
        remark: remark.trim() || undefined,
      }).unwrap();

      onDone?.(res?.message ?? "Recommendation recorded.");
      onClose();
    } catch (e: any) {
      setErr(describeApiError(e) || "That did not work. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-lg bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center">
              <Gavel className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight">
                Record on behalf of {review?.name || "committee member"}
              </h2>
              <p className="text-xs font-semibold text-gray-500">
                {applicantName ? `${applicantName} · ` : ""}their recommendation, keyed by you
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {already ? (
            <div className="flex gap-2 p-3 rounded-2xl bg-amber-50 border border-amber-200 text-[12px] font-semibold text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                {review.name} is already down as{" "}
                <b>{review.decision === "approved" ? "recommending" : "not recommending"}</b>
                {review.recorded_by_name ? ` (recorded by ${review.recorded_by_name})` : ""}. Saving
                replaces that decision — both are kept in the application's review trail.
              </div>
            </div>
          ) : null}

          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
              Their recommendation
            </label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {/* Worded as the committee's own verdict — "approve" is the admin
                  deciding, "recommends" is what the row actually means. */}
              <button
                type="button"
                onClick={() => setDecision("approved")}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border text-sm font-bold transition ${
                  decision === "approved"
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700"
                }`}
              >
                <ThumbsUp className="w-4 h-4" /> Recommends
              </button>
              <button
                type="button"
                onClick={() => setDecision("rejected")}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border text-sm font-bold transition ${
                  decision === "rejected"
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-rose-300 hover:text-rose-700"
                }`}
              >
                <ThumbsDown className="w-4 h-4" /> Does not recommend
              </button>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
              Remark <span className="text-gray-300">· optional</span>
            </label>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={4}
              placeholder="The reason the committee member gave, in their words."
              className="mt-2 w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>

          {/* What the first recommendation on an application actually does. Said
              here rather than discovered afterwards: an approval opens the
              applicant's induction bill, which is money being asked for. */}
          <p className="text-[11px] font-semibold text-gray-500 leading-relaxed">
            The first recommendation recorded on an application moves it — recommended, which lets
            the applicant pay their induction fees, or not recommended, which goes to the chairman to
            finalise. Later ones are recorded against their own member and counted in the tally.
          </p>

          {err ? (
            <div className="flex gap-2 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-[12px] font-semibold text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!decision || saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {already ? "Replace decision" : "Record decision"}
          </button>
        </div>
      </div>
    </div>
  );
}
